import { chartColors, Tag, Typography } from '@components';
import React, { FC, useEffect, useState } from 'react';
import { Bar, BarChart, ResponsiveContainer, XAxis } from 'recharts';

interface DeviceInfo {
  manufacturer?: string;
  name: string;
  architecture: string;
  lastBootTime: number;
  buildNumber: string;
  releaseId?: string;
  servicePackMajorVersion: number;
  servicePackMinorVersion: number;
  locale: string;
  language: string;
  needsReboot: boolean;
  deviceId: number;
  timestamp: number;
}

type OperatingSystem =
  | 'Windows 11'
  | 'Windows 10'
  | 'Windows 8.1'
  | 'Windows 8'
  | 'Windows 7'
  | 'Windows XP'
  | 'Ubuntu'
  | 'Windows Server 2025'
  | 'Windows Server 2022'
  | 'Windows Server 2019'
  | 'Windows Server 2016'
  | 'Windows Server 2012'
  | 'Other';

const OPERATING_SYSTEMS: OperatingSystem[] = [
  'Windows 11',
  'Windows 10',
  'Windows 8.1',
  'Windows 8',
  'Windows 7',
  'Windows XP',
  'Ubuntu',
  'Windows Server 2012',
  'Windows Server 2016',
  'Windows Server 2019',
  'Windows Server 2022',
  'Windows Server 2025',
];

type OSCountWithColor = {
  count: number;
  color: string;
};

interface props {
  data: DeviceInfo[];
  filter?: any;
}

export const DevicesOsChart: FC<props> = ({ data, filter }) => {
  const [chartData, setChartData] = useState<any[]>([]);
  const [osCounts, setOsCounts] = useState<Record<string, OSCountWithColor>>(
    {}
  );
  const [oldestOs, setOldestOs] = useState(0);

  useEffect(() => {
    const tempCounts: Record<string, OSCountWithColor> = {};

    data.forEach((device) => {
      const match = OPERATING_SYSTEMS.find((os) => device.name.includes(os));
      const key: string = match ?? 'Other';

      if (!tempCounts[key]) {
        const colorIndex = Object.keys(tempCounts).length % chartColors.length;
        tempCounts[key] = { count: 1, color: chartColors[colorIndex] };
      } else {
        tempCounts[key].count += 1;
      }
    });
    setOsCounts(tempCounts);

    const rechartsRow: Record<string, number> = {};
    Object.entries(tempCounts).forEach(([os, info]) => {
      rechartsRow[os] = info.count;
    });

    setChartData([rechartsRow]);
    setOldestOs(score());
  }, [data]);

  const score = () => {
    let lowestVersion = null;

    for (const key in osCounts) {
      if (key.startsWith('Windows')) {
        const match = key.match(/\d+/);
        if (match) {
          const version = parseInt(match[0], 10);
          if (lowestVersion === null || version < lowestVersion) {
            lowestVersion = version;
          }
        }
      }
    }
    return lowestVersion ? lowestVersion : 0;
  };

  return (
    <>
      <OsScore lowesOsVersion={oldestOs} />
      <Typography tag="h3" type="subtitle">
        Operating Systems
      </Typography>
      <Typography tag="span" type="label">
        This only works for CMC managed devices.
      </Typography>
      <div className="flex flex-col items-center justify-center h-[80%]">
        <div className="flex flex-col md:flex-row items-center justify-center gap-6">
          <div className="w-[50px] h-[200px]">
            <ResponsiveContainer width="100%">
              <BarChart data={chartData}>
                <XAxis hide dataKey="name" />
                {Object.entries(osCounts).map(([os, { color }]) => (
                  <Bar
                    key={os}
                    dataKey={os}
                    stackId="os"
                    fill={color}
                    barSize={15}
                    onClick={() => filter(os)}
                    className="cursor-pointer"
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-col justify-center max-h-[180px]">
            {Object.entries(osCounts).map(([os, { color, count }]) => (
              <div
                key={os}
                className="flex items-center text-sm space-x-4 mb-1 cursor-pointer"
                onClick={() => filter(os)}
              >
                <div
                  className="w-[15px] h-[15px]"
                  style={{
                    backgroundColor: color,
                  }}
                />
                <span>
                  {os} ({count})
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export const OsScore: FC<any> = ({ lowesOsVersion }) => {
  if (!lowesOsVersion) return <Tag style="success" label="Looks good" />;

  if (lowesOsVersion < 8) {
    return <Tag style="danger" label="You are at risk" />;
  }

  if (lowesOsVersion === 10) {
    <Tag style="bussy" label="Upgrade to windows 11" />;
  }

  return <Tag style="success" label="Looks good" />;
};

export default DevicesOsChart;
